import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  MenuItem,
} from '@mui/material';
import { Homeimages } from '../assets';
import { useMutation } from '@tanstack/react-query';
import { IndividualForm } from '../api/services';

type MentoringType =
  | 'BEGINNER’S PATH TO  EXCELLENCE (0-2yrs)'
  | 'SALES ACCELERATOR( 2-4yrs)'
  | 'EXCLUSIVE PERSONALIZED MENTORing (5+yrs)';

type PreferredMode = 'Online' | 'Offline' | 'Hybrid';
type ExperienceOpt = '0-2years' | '2-4years' | '5+years';

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  currentProfessionStatus: string;
  experience?: ExperienceOpt;
  city?: string;
  mentoringTypes: MentoringType[];
  preferredMode?: PreferredMode;
  preferredStartDate?: string;
  message?: string;
}

type Errors = Partial<Record<keyof FormData, string>> & {
  mentoringTypes?: string;
};
const INITIAL: FormData = {
  name: '',
  email: '',
  phoneNumber: '',
  currentProfessionStatus: '',
  experience: undefined,
  city: '',
  mentoringTypes: [],
  preferredMode: undefined,
  preferredStartDate: undefined,
  message: '',
};

const IndividualMentoringForm: React.FC = () => {
  const [data, setData] = useState<FormData>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});

  // Helpers for mode constraints derived from current selections
  const isBeginner = (t: string) => t.startsWith('BEGINNER’S PATH');
  const isGrowth = (t: string) => t.startsWith('GROWTH ACCELERATOR');

  const mutation = useMutation({
    mutationFn: IndividualForm,
    onSuccess: (res) => {
      alert(res.data?.message || 'Form submitted successfully!');
      setData(INITIAL);
    },
    onError: (err: any) => {
      console.error(err);
      alert('Something went wrong while submitting. Please try again.');
    },
  });



  // Validation
  const validate = (d: FormData): Errors => {
    const e: Errors = {};
    if (!d.name.trim()) e.name = 'Required';
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!d.email.trim()) e.email = 'Required';
    else if (!emailRx.test(d.email)) e.email = 'Invalid email';
    const phoneRx = /^[0-9]{10}$/;
    if (!d.phoneNumber.trim()) e.phoneNumber = 'Required';
    else if (!phoneRx.test(d.phoneNumber)) e.phoneNumber = '10 digits';
    if (!d.currentProfessionStatus.trim())
      e.currentProfessionStatus = 'Required';
    if (!d.mentoringTypes.length) e.mentoringTypes = 'Select at least one';
    return e;
  };

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setData((prev) => ({ ...prev, [key]: value }));

  // visuals to mirror organizational form
  const underlineSx = {
    '& .MuiInputBase-root': { color: '#fff' },
    '& .MuiFormLabel-root': { color: 'rgba(255,255,255,0.9)' },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255,255,255,0.6)',
      opacity: 1,
    },
    // Make dropdown toggle arrow clearly visible on dark bg
    '& .MuiSelect-icon': { color: '#fff' },
    '& .MuiSvgIcon-root': { color: '#fff' },
    // Make date picker calendar icon visible
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
      cursor: 'pointer',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'rgba(255,255,255,0.8)',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottomColor: '#fff',
    },
    '& .MuiInput-underline:after': { borderBottomColor: '#fff' },
  } as const;

  const labelWhite = { color: '#fff', '&.Mui-focused': { color: '#fff' } };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(data);
    setErrors(v);
    if (Object.keys(v).length === 0) {
      console.log('Individual Mentoring Form:', data);
      mutation.mutate(data);
    }
  };

  const toggleMentoringType = (mt: MentoringType) => {
    setData((prev) => {
      const exists = prev.mentoringTypes.includes(mt);
      const nextTypes = exists
        ? prev.mentoringTypes.filter((x) => x !== mt)
        : [...prev.mentoringTypes, mt];

      const offlineOnly = nextTypes.some((t) => isBeginner(t) || isGrowth(t));

      return {
        ...prev,
        mentoringTypes: nextTypes,
        // If any offline-only tier selected, force preferredMode to Offline
        preferredMode: offlineOnly ? 'Offline' : prev.preferredMode,
      };
    });
  };

  // Ensure radio UI respects constraints even after rerenders
  const offlineOnlySelection = data.mentoringTypes.some(
    (t) => isBeginner(t) || isGrowth(t),
  );
  const isRadioDisabled = (mode: PreferredMode) =>
    offlineOnlySelection && mode !== 'Offline';

  return (
    // OUTER: mirror org form container & background
    <Box
      sx={{
        position: 'relative',
        backgroundImage: `url(${Homeimages.popup2})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        bgcolor: '#000',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        overflowY: 'auto',
        scrollbarColor: '#000 #000',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: '#000' },
        '&::-webkit-scrollbar-thumb': { background: '#000' },
      }}
    >
      {/* CARD: match org width, bg, padding */}
      <Box
        sx={{
          width: { xs: 360, sm: 520, md: 720 },
          bgcolor: 'rgba(0,0,0,0.9)',
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          backdropFilter: 'blur(1px)',
        }}
      >
        {/* Logo size & spacing same as org */}
        <Box
          component="img"
          src={Homeimages.rwsignlogo}
          width={90}
          height={90}
          alt="RW"
          sx={{ display: 'block', mx: 'auto', mb: 2 }}
        />

        {/* FORM */}
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Row 1: Name, Email */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Name *"
                placeholder="Ex: John Doe"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name ? ' ' : ' '}
                sx={{
                  ...underlineSx,
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                  },
                }}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Email *"
                placeholder="Ex:example@gmail.com"
                type="email"
                value={data.email}
                onChange={(e) => set('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email ? ' ' : ' '}
                sx={{
                  ...underlineSx,
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                  },
                }}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            {/* Row 2: Phone */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Phone Number *"
                placeholder="Ex: 9876543210"
                inputMode="tel"
                value={data.phoneNumber}
                onChange={(e) => set('phoneNumber', e.target.value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber ? ' ' : ' '}
                sx={{
                  ...underlineSx,
                  mt: -2,
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                  },
                }}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            {/* Current Profession / Status */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="Current Profession / Status *"
                placeholder="Ex: Entrepreneur"
                value={data.currentProfessionStatus}
                onChange={(e) => set('currentProfessionStatus', e.target.value)}
                error={!!errors.currentProfessionStatus}
                helperText={errors.currentProfessionStatus ? ' ' : ' '}
                sx={{
                  ...underlineSx,
                  mt: -2,
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                  },
                }}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            {/* Row 3: City, Experience (select) */}
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                fullWidth
                label="City / Location *"
                placeholder='Ex:"Hyderabad'
                value={data.city}
                onChange={(e) => set('city', e.target.value)}
                helperText=" "
                sx={{
                  ...underlineSx,
                  mt: -2,
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                  },
                }}
                InputLabelProps={{ sx: labelWhite }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="standard"
                select
                fullWidth
                label="Experience in real estate"
                value={data.experience ?? ''}
                onChange={(e) =>
                  set('experience', e.target.value as ExperienceOpt)
                }
                helperText=" "
                // sx={underlineSx}
                sx={{ ...underlineSx, mt: -2 }}
                InputLabelProps={{ sx: labelWhite }}
              >
                <MenuItem value="0-2years"> 0 - 2 yrs</MenuItem>
                <MenuItem value="2-4years">2 - 4 yrs</MenuItem>
                <MenuItem value="5+years">5+ yrs</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} mt={-2}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  sx={{
                    ...labelWhite,
                    mt: 0.8,
                    mb: 2,
                    color: 'rgba(255,255,255,0.6)',
                    '&.Mui-focused': { color: 'rgba(255,255,255,0.6)' },
                  }}
                >
                  Select Mentoring Type *
                </FormLabel>
                <FormGroup
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}
                >
                  {(
                    [
                      'BEGINNER’S PATH TO  EXCELLENCE (0-2yrs)',
                      'SALES ACCELERATOR (2-4yrs)',
                      'EXCLUSIVE PERSONALIZED MENTORING (5+yrs)',
                    ] as MentoringType[]
                  ).map((label) => (
                    <FormControlLabel
                      key={label}
                      control={
                        <Checkbox
                          checked={data.mentoringTypes.includes(label)}
                          onChange={() => toggleMentoringType(label)}
                          sx={{
                            color: 'rgba(255,255,255,0.6)',
                            '&.Mui-checked': { color: 'rgb(255, 255, 255)' },
                            alignSelf: 'flex-start',
                            mt: -1.0,
                          }}
                        />
                      }
                      label={label}
                      sx={{
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(255,255,255,0.6)',
                          lineHeight: 1.4,
                          mt: '2px',
                        },
                      }}
                    />
                  ))}
                </FormGroup>
                {errors.mentoringTypes && (
                  <Typography sx={{ color: '#ff6b6b', fontSize: 12, mt: 0 }}>
                    {errors.mentoringTypes}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {/* Preferred Mode + Preferred Date */}
            <Grid item xs={12} md={6} mt={-1}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  sx={{
                    ...labelWhite,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    color: 'rgba(255,255,255,0.6)',
                    '&.Mui-focused': { color: 'rgba(255,255,255,0.6)' },
                  }}
                >
                  Preferred Mode
                </FormLabel>

                <RadioGroup
                  row
                  value={data.preferredMode ?? ''}
                  onChange={(e) =>
                    set('preferredMode', e.target.value as PreferredMode)
                  }
                  sx={{ mt: 0.8, mb: 2 }}
                >
                  {(['Online', 'Offline', 'Hybrid'] as PreferredMode[]).map(
                    (mode) => (
                      <FormControlLabel
                        key={mode}
                        value={mode}
                        control={
                          <Radio
                            sx={{
                              color: 'rgba(255,255,255,0.6)',
                              '&.Mui-checked': {
                                color: 'rgba(255,255,255,0.6)',
                              },
                            }}
                            disabled={isRadioDisabled(mode)}
                          />
                        }
                        label={mode}
                        sx={{
                          '& .MuiFormControlLabel-label': { color: '#fff' },
                        }}
                      />
                    ),
                  )}
                </RadioGroup>

                {/* Preferred Start Date */}
                <TextField
                  variant="standard"
                  fullWidth
                  type="date"
                  label="Preferred date to start"
                  value={data.preferredStartDate ?? ''}
                  onChange={(e) => set('preferredStartDate', e.target.value)}
                  helperText=" "
                  sx={{
                    ...underlineSx,
                    '& .MuiInputBase-input': {
                      color: 'rgba(255,255,255,0.6)',
                      width: '100%',
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      ...labelWhite,
                      fontSize: '2rem',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                      width: '100%',
                      maxWidth: '100%',
                      display: 'block',
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                    },
                  }}
                />

                {/* Row 7: Message */}
                <Grid item xs={12}>
                  <TextField
                    variant="standard"
                    fullWidth
                    multiline
                    rows={2}
                    label="Message"
                    placeholder="• I am looking to enhance my sales skills.
• I want to develop a confident, high-performing sales mindset.
• I'm looking for structured guidance to improve my performance in luxury sales."
                    value={data.message}
                    onChange={(e) => set('message', e.target.value)}
                    sx={{
                      ...underlineSx,
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                        fontSize: '0.85rem',
                        fontStyle: 'italic',
                      },
                    }}
                    InputLabelProps={{ sx: labelWhite }}
                  />
                </Grid>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} /> {/* empty to mirror org's layout */}
            {/* Submit: match org style */}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                disabled={mutation.isPending}
                sx={{
                  mt: 1.5,
                  textTransform: 'none',
                  background: 'transparent',
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': { background: 'transparent', opacity: 0.9 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 24,
                    letterSpacing: 6,
                    fontWeight: 500,
                    color: '#fff',
                  }}
                >
                                   {mutation.isPending ? 'Submitting...' : '— S u b m i t'}
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default IndividualMentoringForm;
